import { useState, useCallback } from 'react';
import { Property, AppUser } from '../types';
import { searchByCodesApi } from '../services/propertyApiClient';
import { savePropertyApi, createPropertyApi } from '../services/adminPropertyApiClient';
import { decodeId } from '../utils/propertyMappers';

type PropertyWithSource = Property & { sourceCollection: 'ciencuadras' | 'aliados' | 'brokers' };

export const useAdminProperties = (appUser: AppUser | null) => {
  const [foundPropertyForAdmin, setFoundPropertyForAdmin] = useState<PropertyWithSource | PropertyWithSource[] | null>(null);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [propertyAdminMessage, setPropertyAdminMessage] = useState<string | null>(null);

  const searchPropertyAdmin = useCallback(async (propertyCode: string): Promise<string> => {
    if (!propertyCode) {
      const msg = "Por favor, ingrese un código para buscar.";
      setPropertyAdminMessage(msg);
      return msg;
    }
    setIsLoadingSearch(true);
    setFoundPropertyForAdmin(null);
    setPropertyAdminMessage(null);

    try {
      const trimmed = propertyCode.trim();
      let searchTarget = trimmed;

      if (trimmed.startsWith('CCXP-')) {
        const encodedPart = trimmed.replace('CCXP-', '');
        if (encodedPart) {
          searchTarget = decodeId(encodedPart);
        }
      }

      const res = await searchByCodesApi([searchTarget]);
      const items = (res.items || []).map(p => ({ ...p, sourceCollection: 'ciencuadras' as const }));

      if (items.length > 0) {
        if (items.length === 1) {
          setFoundPropertyForAdmin(items[0]);
        } else {
          setFoundPropertyForAdmin(items);
        }
        const countStr = items.length > 1 ? `Se encontraron ${items.length} inmuebles.` : `Inmueble encontrado.`;
        const msg = trimmed.startsWith('CCXP-') ? `Inmueble desenmascarado encontrado.` : `${countStr} '${trimmed}' listo para gestionar.`;
        setPropertyAdminMessage(msg);
        setIsLoadingSearch(false);
        return msg;
      }

      const notFoundMsg = `Error: No se encontró ningún inmueble con el código '${trimmed}'.`;
      setPropertyAdminMessage(notFoundMsg);
      setIsLoadingSearch(false);
      return notFoundMsg;
    } catch (error: any) {
      console.error("Error searching for property:", error);
      const userMsg = error?.message || "Error al buscar el inmueble";
      setPropertyAdminMessage(userMsg);
      setIsLoadingSearch(false);
      return userMsg;
    }
  }, []);

  const savePropertyAdmin = useCallback(async (updatedPropertyData: Partial<Property>) => {
    if (!updatedPropertyData.id) throw new Error("ID de propiedad no encontrado.");
    const { id, ...pureData } = updatedPropertyData;
    await savePropertyApi(id, {
      ...pureData,
      fecha_modificacion_manual: new Date().toISOString(),
    } as any);
  }, []);

  const createPropertyManual = useCallback(async (newProperty: Partial<Property>, _imageFiles: File[] = [], onProgress?: (msg: string) => void) => {
    if (!newProperty.codigo) throw new Error("El código es obligatorio.");
    if (onProgress) onProgress("Guardando ficha técnica...");

    const fullData: Record<string, any> = {
      ...newProperty,
      creado_manualmente: true,
      usuario_creador: appUser?.uid || 'system',
      fecha_agregado_nuevo: new Date().toISOString(),
      status: 'Activo',
      imagenes: newProperty.images || [],
      features: newProperty.features || [],
      codigo_brp_o_inmobiliaria: newProperty.codigo_brp || null,
      nombre_barrio: (newProperty as any).neighborhoodName || null,
      area_construida: Number(newProperty.area_construida_original) || 0,
    };

    delete fullData.images;
    delete fullData.neighborhoodName;

    await createPropertyApi(fullData, newProperty.codigo);
    return { ...fullData, id: newProperty.codigo } as Property;
  }, [appUser]);

  return {
    foundPropertyForAdmin,
    setFoundPropertyForAdmin,
    isLoadingSearch,
    propertyAdminMessage,
    setPropertyAdminMessage,
    searchPropertyAdmin,
    savePropertyAdmin,
    createPropertyManual,
  };
};
